/**
 * One logged entry in full: the big four up top, then every macro and micronutrient it carries
 * (the whole entry or one of its items), with each as a share of the day's target. Lets the user
 * delete it.
 */
import { useMemo, useState, type ReactElement } from 'react';
import type { NutrientTarget } from '@engine/dri';
import { NUTRIENTS } from '@engine/nutrients';
import { formatTime } from '../lib/dates';
import { effectiveLayout, sectionLabel } from '../lib/diaryLayout';
import { itemNutrients, mealNutrients } from '../lib/foodNutrients';
import { SOURCE_LABEL } from '../lib/labels';
import { errorMessage } from '../lib/logger';
import { netCarbs } from '../lib/nutrition';
import type { MealLog, NutrientMap } from '../lib/types';
import { formatNumber } from '../lib/units';
import { useApp } from '../state/app';
import { Icon } from './Icon';
import { NutrientPanel } from './NutrientPanel';
import { Modal, Spinner } from './ui';

const ALL_GROUPS = ['general', 'carbohydrates', 'lipids', 'protein', 'vitamins', 'minerals'] as const;

export function MealDetailModal({ meal, onClose, onChanged, targets }: {
  meal: MealLog;
  onClose: () => void;
  onChanged: () => void;
  /** Daily targets by catalog key, to show each nutrient as a share of the day. */
  targets?: ReadonlyMap<string, NutrientTarget>;
}): ReactElement {
  const { source, toast, settings } = useApp();
  const [busy, setBusy] = useState<boolean>(false);
  const [confirming, setConfirming] = useState<boolean>(false);
  /** -1 = the whole entry, else an item index. */
  const [part, setPart] = useState<number>(-1);
  const values: NutrientMap = useMemo(() => {
    const item = part >= 0 ? meal.items[part] : undefined;
    return item ? itemNutrients(item) : mealNutrients(meal);
  }, [meal, part]);
  const known: number = NUTRIENTS.filter((n) => values[n.key] !== undefined).length;

  const remove = async (): Promise<void> => {
    setBusy(true);
    try {
      await source.deleteMeal(meal.id);
      toast('Meal deleted');
      onChanged();
      onClose();
    } catch (err) {
      toast(errorMessage(err), 'err');
    } finally {
      setBusy(false);
    }
  };

  const v = (k: string): number | undefined => values[k];
  const big = (label: string, k: string, unit: string, color?: string, extra?: string): ReactElement => (
    <div>
      <span style={color ? { color } : undefined}>{label}</span>
      <b className="num">{v(k) === undefined ? '—' : formatNumber(v(k) as number, k === 'energy_kcal' ? 0 : 1)}</b>
      <small>{unit}{extra ? ` · ${extra}` : ''}</small>
    </div>
  );
  const carbs: number | undefined = v('carbs_g');

  return (
    <Modal title={meal.description} onClose={onClose} width={760} footer={
      confirming ? (
        <>
          <span className="muted" style={{ marginRight: 'auto', fontWeight: 600 }}>Delete this meal for good?</span>
          <button type="button" className="btn" onClick={() => setConfirming(false)}>Keep it</button>
          <button type="button" className="btn btn-danger" onClick={() => void remove()} disabled={busy}>{busy ? <Spinner /> : <Icon name="trash" size={16} />} Delete</button>
        </>
      ) : (
        <>
          <button type="button" className="btn btn-danger" style={{ marginRight: 'auto' }} onClick={() => setConfirming(true)}><Icon name="trash" size={16} /> Delete</button>
          <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
        </>
      )
    }>
      <div className="row" style={{ marginBottom: 14 }}>
        <span className="chip">{sectionLabel(effectiveLayout(settings.diary_layout, settings.energy_mode), meal)}</span>
        <span className="chip">{formatTime(meal.eaten_at)}</span>
        <span className="chip">{SOURCE_LABEL[meal.source]}</span>
        {meal.ai_confidence !== null && <span className="chip">confidence {Math.round(meal.ai_confidence * 100)}%</span>}
      </div>

      {meal.items.length > 1 && (
        <label className="field" style={{ marginBottom: 12, maxWidth: 420 }}><span>Show nutrients for</span>
          <select className="input" value={part} onChange={(e) => setPart(Number(e.target.value))}>
            <option value={-1}>The whole entry ({meal.items.length} items)</option>
            {meal.items.map((it, i) => <option key={i} value={i}>{it.quantity} {it.unit} {it.name}</option>)}
          </select>
        </label>
      )}

      <div className="meal-macros">
        {big('Calories', 'energy_kcal', 'kcal', 'var(--energy)')}
        {big('Protein', 'protein_g', 'g', 'var(--protein)')}
        {big('Carbs', 'carbs_g', 'g', 'var(--carbs)', carbs !== undefined ? `${formatNumber(netCarbs(carbs, v('fiber_g') ?? null), 1)} g net` : undefined)}
        {big('Fat', 'fat_g', 'g', 'var(--fat)')}
      </div>

      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
        <strong style={{ fontSize: 14 }}>Every nutrient{part >= 0 ? ' in this item' : ''}</strong>
        <span className="faint" style={{ fontSize: 12.5, fontWeight: 600 }}>{known} of {NUTRIENTS.length} known{targets && targets.size > 0 ? ' · % of your daily target' : ''}</span>
      </div>
      <NutrientPanel values={values} targets={targets && targets.size > 0 ? targets : undefined} showSources={false} defaultOpen={[...ALL_GROUPS]} />
      {known <= 9 && (
        <p className="cite" style={{ margin: '8px 0 0' }}>
          This was logged with only the basics (a quick add or an AI estimate), so vitamins and minerals are unknown (“—”), not zero.
          Foods from the database or a scanned label carry the full breakdown.
        </p>
      )}

      {meal.items.length > 0 && (
        <div style={{ overflowX: 'auto', marginTop: 18 }}>
          <table className="draft-table">
            <thead><tr><th>Food</th><th>Amount</th><th>Calories</th><th>Protein g</th><th>Carbs g</th><th>Fat g</th></tr></thead>
            <tbody>
              {meal.items.map((it, i) => (
                <tr key={i} onClick={() => setPart(meal.items.length > 1 ? i : -1)} style={{ cursor: meal.items.length > 1 ? 'pointer' : undefined }}>
                  <td style={{ fontWeight: 600 }}>{it.name}{it.preparation ? <span className="faint"> · {it.preparation}</span> : null}</td>
                  <td className="num">{it.quantity} {it.unit}{it.grams ? <span className="faint"> ({Math.round(it.grams)} g)</span> : null}</td>
                  <td className="num">{Math.round(it.calories)}</td>
                  <td className="num">{formatNumber(it.protein_g, 1)}</td>
                  <td className="num">{formatNumber(it.carbs_g, 1)}</td>
                  <td className="num">{formatNumber(it.fat_g, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
